import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Box,
  Chip,
  DialogContentText,
  useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { SavedSearch, Location } from '../../models';
import { apiClient } from '../../services/ApiClient';
import { useSearchContext } from '../../contexts/SearchContext';

// --- Inner component to handle fetching location name ---
interface SavedSearchListItemProps {
  search: SavedSearch;
  onLoad: (searchId: string) => void;
  onDelete: (searchId: string) => void;
}

const SavedSearchListItem: React.FC<SavedSearchListItemProps> = ({ search, onLoad, onDelete }) => {
  const theme = useTheme();
  const [locationName, setLocationName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);

    apiClient.getLocationById(search.parameters.locationId)
      .then((locationData: Location) => {
        if (isMounted) {
          setLocationName(locationData.name);
          setIsLoading(false);
        }
      })
      .catch(err => {
        console.error(`Failed to fetch location name for ID ${search.parameters.locationId}:`, err);
        if (isMounted) {
          setError('Failed to load location name');
          setLocationName('Error');
          setIsLoading(false);
        }
      });

    return () => { isMounted = false; };
  }, [search.parameters.locationId]);

  const formatMonthYear = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  };

  return (
    <ListItem
      alignItems="flex-start"
      secondaryAction={
        <Box>
          <IconButton
            edge="end"
            aria-label="load"
            onClick={() => onLoad(search.id)}
            sx={{ color: theme.palette.success.main }}
          >
            <FolderOpenIcon />
          </IconButton>
          <IconButton
            edge="end"
            aria-label="delete"
            onClick={() => onDelete(search.id)}
            sx={{ color: theme.palette.error.main }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      }
      sx={{ py: 1.5 }}
    >
      <ListItemText
        primary={
          <Typography variant="subtitle1" component="div">
            {search.name}
          </Typography>
        }
        secondary={
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Location: {isLoading ? 'Loading...' : locationName || 'Unknown'} (ID: {search.parameters.locationId})
              {error && <Typography variant="caption" color="error" sx={{ ml: 1 }}>({error})</Typography>}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Month: {formatMonthYear(search.parameters.dateOfInterest)}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {search.parameters.services.map((service) => (
                <Chip
                  key={service.id}
                  label={service.name}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        }
      />
    </ListItem>
  );
};

interface SavedSearchesDialogProps {
  open: boolean;
  onClose: () => void;
}

const SavedSearchesDialog: React.FC<SavedSearchesDialogProps> = ({ open, onClose }) => {
  // Use the enhanced SearchContext instead of useSearchManagement hook
  const { savedSearches, loadSearch, deleteSearch } = useSearchContext();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const searchBeingDeleted = deleteConfirmId 
    ? savedSearches.find(s => s.id === deleteConfirmId) 
    : null;

  const confirmDelete = () => {
    if (deleteConfirmId) {
      deleteSearch(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmId(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Saved Searches</DialogTitle>
        <DialogContent dividers>
          {savedSearches.length === 0 ? (
            <Typography align="center" sx={{ py: 2 }}>
              You don't have any saved searches yet.
            </Typography>
          ) : (
            <List sx={{ width: '100%', p: 0 }}>
              {savedSearches.map((search, index) => (
                <React.Fragment key={search.id}>
                  {index > 0 && <Divider component="li" variant="inset" />}
                  <SavedSearchListItem
                    search={search}
                    onLoad={loadSearch}
                    onDelete={setDeleteConfirmId}
                  />
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteConfirmId}
        onClose={cancelDelete}
      >
        <DialogTitle>Delete Saved Search</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{searchBeingDeleted?.name || 'this search'}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SavedSearchesDialog;
